import { avvColumns, avvSorting, serviceColumns, storageLocationColumns } from '@Constants/TableColumns';
import { EnumWeighing, useWeighing } from '@Hooks/Orders';
import type { ApiAvv, ApiOrderPosition, ApiService, ApiStorageLocation, ApiWeighingSlip } from '@Model';
import { EnumFrontendLayoutSetting, EnumServiceType, EnumTaxationMode, EnumUnit, shortUnitOptions } from '@Model';
import { FrameEditAvv, FrameEditService, FrameEditStorageLocation } from '@Modules/Stammdaten';
import {
    type Awaitable,
    Badge,
    Button,
    Card,
    defaultIconSize,
    EnumOperation,
    FlexRow,
    getSpacings,
    Grid,
    GridColumns,
    isLinked,
    isOk,
    type ISubFrameBodyProps,
    LabeledDiv,
    ListRequest,
    Modal,
    NumberInput,
    renderDecimal,
    replaceInList,
    Select,
    SingleSelect,
    type Spacings,
    useGuiBinder,
    useLinkedFieldInFrame,
    usePrefilled,
    useTranslate,
} from '@synqo/frontend-component-library';
import { IconScale } from '@tabler/icons-react';

import { LinkedFieldWithLayoutSettings } from '@Components/LinkedFieldWithLayoutSettings';
import { useCallback, useRef } from 'react';
import { usePaymentMode } from './usePaymentMode';
import { useScaleSelection } from './useScaleSelection';
import { useWeighingProcess } from './useWeighingProcess';

const cardMargin: Spacings = { top: 'md' };
const cardPadding: Spacings = getSpacings(cardMargin, 'none');

export interface WeighingModalProps extends ISubFrameBodyProps<ApiOrderPosition> {
    isOpen: boolean;
    weighingType: EnumWeighing;
    priceDisplay: EnumTaxationMode;
    weighingSlipId?: string;
    isCashPayment?: boolean;
    onClose: () => void;
    saveAndNavigateToHofliste: () => Awaitable<boolean>;
    doAnotherWeighing: () => void;
    approveAndPrint: () => void;
}

export function WeighingModal(props: WeighingModalProps) {
    const i18n = useTranslate('');

    const binder = useGuiBinder<ApiOrderPosition>(props);
    const requestUpdateState = props.requestUpdateState;

    const apiOrderPosition = props.data;
    const weighingSlipId = useRef(props.weighingSlipId);
    const weighingSlip = apiOrderPosition.weighing_slips.find((ws) => ws.id === weighingSlipId.current);

    const getPrefilledWeighingSlip = usePrefilled<ApiWeighingSlip>(
        `${props.apiResourcePath}/${props.data.id}/weighing_slips`,
    );
    const { scalesOptions, selectedScale, setSelectedScale, hasMultipleScales, canUseAutomaticWeighing, weightUnit } =
        useScaleSelection(weighingSlip?.unit);
    const { setWeight, getWeight } = useWeighing();

    const { isComplete, bothWeightsAreSet, highlightBrutto, highlightTare, label } = useWeighingProcess(
        props.weighingType,
        weighingSlip?.gross_weight,
        weighingSlip?.tare_weight,
    );

    const { ensurePaymentMode } = usePaymentMode(apiOrderPosition.price_per_unit, requestUpdateState);

    const addManualWeight = useCallback(
        async (value: number, weighingType: EnumWeighing) => {
            return requestUpdateState(async (latest, saveData) => {
                let activeWeighingSlip: ApiWeighingSlip | undefined;

                if (weighingSlipId.current === undefined) {
                    const prefilledResult = await getPrefilledWeighingSlip(null);
                    if (!isOk(prefilledResult)) {
                        return { data: undefined, messages: prefilledResult.messages };
                    }

                    activeWeighingSlip = prefilledResult.data;
                }

                if (activeWeighingSlip === undefined) {
                    activeWeighingSlip = latest.weighing_slips.find((ws) => ws.id === weighingSlipId.current);
                    if (activeWeighingSlip === undefined) {
                        return { data: undefined, messages: [] };
                    }
                }

                weighingSlipId.current = activeWeighingSlip.id;
                setWeight(activeWeighingSlip, weighingType, value);

                return saveData({
                    ...latest,
                    weighing_slips: replaceInList(latest.weighing_slips, activeWeighingSlip),
                });
            });
        },
        [requestUpdateState, getPrefilledWeighingSlip, setWeight],
    );

    const doWeighing = useCallback(async () => {
        if (selectedScale === undefined) {
            return;
        }
        const scaleResponse = await getWeight(selectedScale);
        return requestUpdateState(async (latest, saveData) => {
            let activeWeighingSlip: ApiWeighingSlip | undefined;
            if (weighingSlipId.current === undefined) {
                const prefilledResult = await getPrefilledWeighingSlip(scaleResponse);
                if (!isOk(prefilledResult)) {
                    return { data: undefined, messages: prefilledResult.messages };
                }

                activeWeighingSlip = prefilledResult.data;
                weighingSlipId.current = activeWeighingSlip.id;
                return {
                    data: {
                        ...latest,
                        weighing_slips: [...latest.weighing_slips, activeWeighingSlip],
                    },
                    messages: [],
                };
            } else {
                activeWeighingSlip = latest.weighing_slips.find((ws) => ws.id === weighingSlipId.current);
                if (activeWeighingSlip === undefined || scaleResponse === undefined) {
                    return { data: latest, messages: [] };
                }

                setWeight(activeWeighingSlip, props.weighingType, scaleResponse);

                return saveData({
                    ...latest,
                    weighing_slips: replaceInList(latest.weighing_slips, activeWeighingSlip),
                });
            }
        });
    }, [selectedScale, getWeight, requestUpdateState, getPrefilledWeighingSlip, setWeight, props.weighingType]);

    const linkStorageLocation = useLinkedFieldInFrame(props, 'storage_location', FrameEditStorageLocation);
    const linkAvv = useLinkedFieldInFrame(props, 'avv', FrameEditAvv);

    const linkService = useLinkedFieldInFrame(props, 'service', FrameEditService);
    const loadLinkableServices = linkService.linkApi.loadLinkableObjects;
    const loadOnlyMaterialServices = useCallback(
        (r: ListRequest) => {
            if (!r.filter_columns) {
                r.filter_columns = {
                    filters: [],
                    operation: 0,
                };
            }

            r.filter_columns.filters.push({
                field: 'type',
                value: EnumServiceType.Material.toString(),
                operator: EnumOperation.Equal,
            });

            return loadLinkableServices(r);
        },
        [loadLinkableServices],
    );

    return (
        <Modal isOpen={props.isOpen} title={label} onClose={props.onClose}>
            <Card name={'position_data'} border={'withoutBorder'} padding="none" collapsable={false}>
                <Grid columns={12}>
                    <GridColumns spans={[4, 4, 4]}>
                        <LinkedFieldWithLayoutSettings<ApiService>
                            layoutSettingsContext={EnumFrontendLayoutSetting.ServicesTableLayout}
                            {...binder.bindToLinkProperty({
                                ...linkService.linkApi,
                                loadLinkableObjects: loadOnlyMaterialServices,
                            })}
                            columns={serviceColumns}
                            label="Leistung"
                            highlighted={!isLinked(apiOrderPosition.service)}
                        />
                        <LinkedFieldWithLayoutSettings<ApiStorageLocation>
                            layoutSettingsContext={EnumFrontendLayoutSetting.StorageLocationsTableLayout}
                            {...binder.bindToLinkProperty(linkStorageLocation.linkApi)}
                            {...linkStorageLocation.events}
                            columns={storageLocationColumns}
                            label="Lagerort"
                        />
                        <LinkedFieldWithLayoutSettings<ApiAvv>
                            layoutSettingsContext={EnumFrontendLayoutSetting.AvvsTableLayout}
                            {...binder.bindToLinkProperty(linkAvv.linkApi)}
                            {...linkAvv.events}
                            columns={avvColumns}
                            sorting={avvSorting}
                            label={i18n('Avv')}
                        />
                    </GridColumns>
                    <GridColumns spans={[3, 1, 4, 4]}>
                        <NumberInput
                            {...binder.bindToProp('quantity')}
                            decimals={apiOrderPosition.service.decimal_places_amount}
                            label="Menge"
                            locked={apiOrderPosition.weighing_slips.length > 0}
                        />
                        <Select<EnumUnit>
                            name="unit"
                            items={shortUnitOptions}
                            value={apiOrderPosition.service.unit}
                            onChange={() => false}
                            label={i18n('Einheit')}
                            locked
                        />
                        <NumberInput
                            {...binder.bindToProp('price_per_unit', ensurePaymentMode)}
                            decimals={apiOrderPosition.service.decimal_places_price}
                            label="Einzelpreis in €"
                        />
                        {props.priceDisplay === EnumTaxationMode.PriceIsGross ? (
                            <LabeledDiv label="Gesamtpreis in € (Brutto)" htmlFor="">
                                <Badge type="input" variant="primary" size="sm">
                                    {`${renderDecimal(apiOrderPosition.total_gross_price, apiOrderPosition.service.decimal_places_price)} €`}
                                </Badge>
                            </LabeledDiv>
                        ) : (
                            <LabeledDiv label="Gesamtpreis in € (Netto)" htmlFor="">
                                <Badge type="input" variant="primary" size="sm">
                                    {`${renderDecimal(apiOrderPosition.total_net_price, apiOrderPosition.service.decimal_places_price)} €`}
                                </Badge>
                            </LabeledDiv>
                        )}
                    </GridColumns>
                </Grid>
            </Card>
            <Card name={'weighing_actions'} padding={cardPadding} margin={cardMargin} collapsable={false}>
                <Grid columns={12}>
                    <GridColumns spans={hasMultipleScales ? [6, 6] : [12]}>
                        {hasMultipleScales && (
                            <SingleSelect
                                name={'select_scale'}
                                label={'Waage'}
                                items={scalesOptions}
                                value={selectedScale?.id ?? ''}
                                onChange={(value) => {
                                    setSelectedScale(value);
                                    return true;
                                }}
                                enableFlex
                            />
                        )}
                        <FlexRow grow>
                            <LabeledDiv
                                label={'Wiegevorgang'}
                                htmlFor={'do_weighing'}
                                className={'flex flex-col flex-grow w-full'}
                            >
                                <Button
                                    onClick={doWeighing}
                                    label={'do_weighing'}
                                    locked={!canUseAutomaticWeighing}
                                    className={'flex-grow w-full'}
                                >
                                    <IconScale size={defaultIconSize} />
                                    &nbsp;Wiegen
                                </Button>
                            </LabeledDiv>
                        </FlexRow>
                    </GridColumns>
                </Grid>
            </Card>

            <Card name="weights" padding={cardPadding} margin={cardMargin} collapsable={false}>
                <Grid>
                    <GridColumns spans={[4, 4, 4]}>
                        <NumberInput
                            value={weighingSlip?.gross_weight ?? 0}
                            type={'number'}
                            name={'measured_gross_weight'}
                            label={`Brutto-Gewicht in ${weightUnit}`}
                            onChange={() => true}
                            onConfirm={(value) => {
                                addManualWeight(value, EnumWeighing.Gross);
                                return true;
                            }}
                            highlighted={highlightBrutto}
                            decimals={3}
                        />

                        <NumberInput
                            value={weighingSlip?.tare_weight ?? 0}
                            name={'measured_tare_weight'}
                            type={'number'}
                            label={`Tara-Gewicht in ${weightUnit}`}
                            onChange={() => true}
                            onConfirm={(value) => {
                                addManualWeight(value, EnumWeighing.Tare);
                                return true;
                            }}
                            highlighted={highlightTare}
                            decimals={3}
                        />

                        <LabeledDiv label={`Netto-Gewicht in ${weightUnit}`} htmlFor="">
                            <Badge type="input" variant="primary" size="sm">
                                {`${renderDecimal(weighingSlip?.net_weight ?? 0, 3)} ${weightUnit}`}
                            </Badge>
                        </LabeledDiv>
                    </GridColumns>
                </Grid>
            </Card>
            <Card name="footer" padding={cardPadding} margin={cardMargin} collapsable={false}>
                <FlexRow maximize grow>
                    {bothWeightsAreSet ? (
                        <>
                            <Button onClick={props.onClose} locked={!isComplete}>
                                Speichern & zurück zum Auftrag
                            </Button>
                            <Button onClick={props.approveAndPrint} locked={!isComplete}>
                                {props.isCashPayment ? 'Freigeben & kassieren' : 'Freigeben & drucken'}
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button onClick={props.doAnotherWeighing} locked={!isComplete}>
                                Weiteren Vorgang erfassen
                            </Button>
                            <Button onClick={props.saveAndNavigateToHofliste} locked={!isComplete}>
                                In Hofliste speichern
                            </Button>
                        </>
                    )}
                </FlexRow>
            </Card>
        </Modal>
    );
}
